import { VideoEditorLogger } from '../../../utility/VideoEditorLogger';
import { AnchorResolver } from '../../models/layout/AnchorResolver';

import type { Timeline } from '../../models/Timeline';

export interface TimeRange {
  start: number;
  end: number;
}

/**
 * Snaps each range's start/end to the nearest scene boundary when it lies
 * within `sceneSnapThresholdFrames` of that boundary. Ranges are snapped
 * independently — their order is preserved and they are never merged.
 *
 * A snapped range whose end would collapse to (or past) its start is left at
 * its original values.
 *
 * Extracted from CropTimelineCommand so the reel-crop path can reuse the exact
 * same snapping behavior.
 */
export function snapRangesToSceneBoundaries(
  timeline: Timeline,
  ranges: TimeRange[],
  sceneSnapThresholdFrames: number,
): TimeRange[] {
  const threshold = Math.max(0, sceneSnapThresholdFrames);
  if (threshold === 0) {
    return ranges; // No adjustment when threshold is 0
  }

  const scenes = [...timeline.scenes];
  if (scenes.length === 0) {
    return ranges; // No scenes to snap to
  }

  const anchorResolver = new AnchorResolver();
  const frameDuration = 1 / timeline.framerate;
  const snapDistanceSeconds = threshold * frameDuration;

  // Get all scene boundaries
  const sceneBoundaries: number[] = [];
  for (const scene of scenes) {
    try {
      const sceneStart = anchorResolver.resolve(scene.anchorStart, timeline);
      const sceneEnd = anchorResolver.resolve(scene.anchorEnd, timeline);
      sceneBoundaries.push(sceneStart, sceneEnd);
    } catch {
      // Skip scenes that can't be resolved
      continue;
    }
  }

  // Remove duplicates and sort
  const uniqueBoundaries = [...new Set(sceneBoundaries)].sort((a, b) => a - b);

  // Adjust each range
  return ranges.map(range => {
    let adjustedStart = range.start;
    let adjustedEnd = range.end;

    // Find closest scene boundary for start
    let closestStartBoundary: number | null = null;
    let minStartDistance = Infinity;
    for (const boundary of uniqueBoundaries) {
      const distance = Math.abs(range.start - boundary);
      if (distance <= snapDistanceSeconds && distance < minStartDistance) {
        closestStartBoundary = boundary;
        minStartDistance = distance;
      }
    }
    if (closestStartBoundary !== null) {
      adjustedStart = timeline.snapTimeToFrame(closestStartBoundary);
    }

    // Find closest scene boundary for end
    let closestEndBoundary: number | null = null;
    let minEndDistance = Infinity;
    for (const boundary of uniqueBoundaries) {
      const distance = Math.abs(range.end - boundary);
      if (distance <= snapDistanceSeconds && distance < minEndDistance) {
        closestEndBoundary = boundary;
        minEndDistance = distance;
      }
    }
    if (closestEndBoundary !== null) {
      adjustedEnd = timeline.snapTimeToFrame(closestEndBoundary);
    }

    if (adjustedEnd <= adjustedStart) {
      // revert to original
      return range;
    }

    if (adjustedStart !== range.start || adjustedEnd !== range.end) {
      VideoEditorLogger.logTimelineOperation(
        'crop_timeline_command__scene_snap_adjustment',
        {
          message: 'Adjusted range to scene boundary',
          original: range,
          adjusted: { start: adjustedStart, end: adjustedEnd },
          threshold,
        },
      );
    }

    return { start: adjustedStart, end: adjustedEnd };
  });
}
